import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "../../../utils/function";
import { MERGE_LOGS, PROFILES } from "./data";
import { card, PageHeader, Pill, primaryBtn } from "./ui";

// Merge Logs (/unify/merge-logs): how source records were matched, merged,
// enriched or left unresolved during profile generation. Sample rows.

const RESULTS = ["All", "Success", "No Matches"];

const MergeLogsPage: React.FC = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [result, setResult] = useState("All");
  const query = q.trim().toLowerCase();
  const rows = MERGE_LOGS.filter(
    (r) => (result === "All" || r.result === result) && (!query || [r.source, r.profile, r.rule, r.action].some((v) => v.toLowerCase().includes(query)))
  );
  const profileId = (name: string) => PROFILES.find((p) => p.name === name)?.id;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <PageHeader crumbs={["Unify", "Merge Logs"]} back="/unify/dashboard" />
      <div className="bg-gray-100/80 dark:bg-gray-900/40 border border-t-0 border-gray-200 dark:border-gray-800 rounded-b-xl px-4 sm:px-6 py-6 space-y-5">
        <div>
          <h2 className="text-xl sm:text-2xl text-gray-900 dark:text-gray-100">Merge overview</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Review how source records were matched, merged, enriched or left unresolved during profile generation</p>
        </div>
        <div className={cn(card, "p-4")}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative w-full sm:w-72">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search Sources or fields. . ."
                className="w-full h-11 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 pl-10 pr-3 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-indigo-400"
              />
            </div>
            <div className="relative sm:ml-auto w-full sm:w-56">
              <select
                value={result}
                onChange={(e) => setResult(e.target.value)}
                className="w-full appearance-none h-11 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 pl-3 pr-9 text-sm text-gray-900 dark:text-gray-100 outline-none"
              >
                {RESULTS.map((r) => (
                  <option key={r} value={r}>
                    Result: {r}
                  </option>
                ))}
              </select>
              <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
          </div>
          <div className="overflow-x-auto mt-5">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="text-left text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-800">
                  {["Logged at", "Source", "Profile", "Match Rule", "Action", "Result"].map((h) => (
                    <th key={h} className="font-medium py-3 px-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const pid = profileId(r.profile);
                  return (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-4 px-3 text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.at}</td>
                      <td className="py-4 px-3 text-gray-900 dark:text-gray-100">{r.source}</td>
                      <td className="py-4 px-3">
                        {pid ? (
                          <button type="button" onClick={() => navigate(`/unify/unified-profiles/${pid}`)} className="text-indigo-700 dark:text-indigo-400 hover:underline">
                            {r.profile}
                          </button>
                        ) : (
                          r.profile
                        )}
                      </td>
                      <td className="py-4 px-3 text-gray-900 dark:text-gray-100">{r.rule}</td>
                      <td className="py-4 px-3">
                        <Pill>{r.action}</Pill>
                      </td>
                      <td className="py-4 px-3">
                        <Pill tone={r.result === "Success" ? "green" : "gray"} dot>
                          {r.result}
                        </Pill>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === 0 && <p className="py-10 text-center text-sm text-gray-500">No merge logs match these filters.</p>}
          </div>
        </div>
        <div className="flex justify-end">
          <button type="button" onClick={() => navigate("/unify/setup")} className={primaryBtn}>
            Review
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeLogsPage;
